import React from 'react'

export interface GalleryImageGridProps {
  images: { id: number; image_url: string; caption?: string | null }[]
  onSelect?: (id: number) => void
  onSetCover?: (id: number) => void
  onDelete?: (ids: number[]) => void
  onEditCaption?: (id: number) => void
  onOrderChange?: (orderedIds: number[]) => void
  loading?: boolean
  height?: number // px height of the scroll container (default: 600)
  minTileSize?: number // px minimum tile size before increasing columns (default: 180)
  overscan?: number // rows to render beyond viewport (default: 3)
}

type NumSet = Set<number>

export default function GalleryImageGrid({
  images,
  onSelect,
  onSetCover,
  onDelete,
  onEditCaption,
  onOrderChange,
  loading = false,
  height = 600,
  minTileSize = 180,
  overscan = 3,
}: GalleryImageGridProps) {
  // Local order state to enable drag-to-reorder without mutating parent data
  const [order, setOrder] = React.useState<number[]>(() => images.map(i => i.id))
  React.useEffect(() => {
    const incoming = images.map(i => i.id)
    // If ids changed (added/removed), reconcile while preserving current relative order
    setOrder(prev => {
      const prevSet = new Set(prev)
      const next = prev.filter(id => incoming.includes(id))
      for (const id of incoming) if (!prevSet.has(id)) next.push(id)
      onOrderChange?.(next)
      return next
    })
  }, [images])

  const imageById = React.useMemo(() => {
    const m = new Map<number, { id: number; image_url: string; caption?: string | null }>()
    for (const img of images) m.set(img.id, img)
    return m
  }, [images])

  // Selection state with shift-range
  const [selected, setSelected] = React.useState<NumSet>(() => new Set())
  const [lastIndex, setLastIndex] = React.useState<number | null>(null)

  const toggleSelect = (index: number, e?: React.MouseEvent) => {
    const id = order[index]
    setSelected(prev => {
      const next = new Set(prev)
      const isRange = e?.shiftKey && lastIndex !== null
      const isToggle = e?.metaKey || e?.ctrlKey
      if (isRange) {
        const [start, end] = [lastIndex!, index].sort((a, b) => a - b)
        for (let i = start; i <= end; i++) next.add(order[i])
      } else if (isToggle) {
        if (next.has(id)) next.delete(id)
        else next.add(id)
      } else {
        next.clear()
        next.add(id)
      }
      return next
    })
    setLastIndex(index)
    onSelect?.(id)
  }

  const clearSelection = () => setSelected(new Set())
  const selectAll = () => setSelected(new Set(order))

  // Keyboard handling
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.key === 'a' || e.key === 'A') && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      selectAll()
      return
    }
    if (e.key === 'Escape') {
      clearSelection()
      return
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (selected.size && onDelete) onDelete(Array.from(selected))
      return
    }
  }

  // Virtualization calculations
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const [containerWidth, setContainerWidth] = React.useState(0)

  const GAP = 8 // px, matches tailwind gap-2
  const [scrollTop, setScrollTop] = React.useState(0)
  const [clientHeight, setClientHeight] = React.useState(height)

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    setScrollTop(el.scrollTop)
    setClientHeight(el.clientHeight)
  }

  React.useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const update = () => {
      setContainerWidth(el.clientWidth)
      setClientHeight(el.clientHeight)
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const columns = React.useMemo(() => {
    if (!containerWidth) return 1
    const cols = Math.max(1, Math.floor(containerWidth / Math.max(80, minTileSize)))
    return cols
  }, [containerWidth, minTileSize])

  const tileSize = React.useMemo(() => {
    if (!containerWidth || columns === 0) return minTileSize
    // available width minus gaps between columns
    const totalGaps = GAP * (columns - 1)
    return Math.floor((containerWidth - totalGaps) / columns)
  }, [containerWidth, columns, GAP, minTileSize])

  const rowHeight = tileSize + GAP
  const rowCount = Math.ceil(order.length / Math.max(columns, 1))
  const startRow = Math.max(0, Math.floor(scrollTop / Math.max(rowHeight, 1)) - overscan)
  const endRow = Math.min(
    rowCount,
    Math.ceil((scrollTop + clientHeight) / Math.max(rowHeight, 1)) + overscan
  )

  const visible: number[] = []
  for (let r = startRow; r < endRow; r++) {
    for (let c = 0; c < columns; c++) {
      const idx = r * columns + c
      if (idx >= order.length) break
      visible.push(idx)
    }
  }

  // Drag & drop reorder (HTML5 DnD)
  const dragFrom = React.useRef<number | null>(null)
  const handleDragStart = (index: number, e: React.DragEvent) => {
    dragFrom.current = index
    e.dataTransfer.effectAllowed = 'move'
    try {
      e.dataTransfer.setData('text/plain', String(order[index]))
    } catch {}
  }
  const handleDragOver = (index: number, e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }
  const handleDrop = (index: number, _e: React.DragEvent) => {
    const from = dragFrom.current
    dragFrom.current = null
    if (from === null || from === index) return
    setOrder(prev => {
      const next = prev.slice()
      const [moved] = next.splice(from, 1)
      next.splice(index, 0, moved)
      onOrderChange?.(next)
      return next
    })
  }

  if (loading) {
    return (
      <div className="w-full" style={{ height }}>
        <div className="h-full w-full animate-pulse grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="aspect-square rounded bg-muted/70" />
          ))}
        </div>
      </div>
    )
  }

  if (!order.length) {
    return (
      <div className="flex items-center justify-center border rounded-md text-sm text-muted-foreground" style={{ height }}>
        No images yet
      </div>
    )
  }

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      onKeyDown={onKeyDown}
      tabIndex={0}
      className="relative w-full overflow-auto focus:outline-none"
      style={{ height }}
    >
      {/* Spacer to create scroll height */}
      <div style={{ height: Math.max(rowCount * rowHeight - GAP, 0), position: 'relative' }}>
        {visible.map((idx) => {
          const row = Math.floor(idx / columns)
          const col = idx % columns
          const left = col * (tileSize + GAP)
          const top = row * rowHeight
          const id = order[idx]
          const img = imageById.get(id)
          if (!img) return null
          const isSelected = selected.has(id)
          return (
            <div
              key={id}
              role="button"
              className={`group absolute rounded-md border bg-card overflow-hidden ${
                isSelected ? 'ring-2 ring-primary border-primary' : 'border-border'
              }`}
              style={{ width: tileSize, height: tileSize, left, top }}
              draggable
              onDragStart={(e) => handleDragStart(idx, e)}
              onDragOver={(e) => handleDragOver(idx, e)}
              onDrop={(e) => handleDrop(idx, e)}
              onClick={(e) => toggleSelect(idx, e)}
            >
              <img
                src={img.image_url}
                alt={img.caption || ''}
                className="object-cover w-full h-full pointer-events-none select-none"
                loading="lazy"
              />
              {/* Hover overlay actions */}
              <div className="absolute inset-x-0 bottom-0 p-1.5 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex items-center justify-between text-white text-xs">
                  <div className="truncate pr-2">{img.caption || 'Untitled'}</div>
                  <div className="flex items-center gap-1">
                    <button
                      className="px-1 py-0.5 rounded bg-white/20 hover:bg-white/30"
                      onClick={(e) => {
                        e.stopPropagation()
                        onSetCover?.(id)
                      }}
                      title="Set cover"
                    >
                      Cover
                    </button>
                    <button
                      className="px-1 py-0.5 rounded bg-white/20 hover:bg-white/30"
                      onClick={(e) => {
                        e.stopPropagation()
                        onEditCaption?.(id)
                      }}
                      title="Edit caption"
                    >
                      Edit
                    </button>
                    <button
                      className="px-1 py-0.5 rounded bg-destructive/80 hover:bg-destructive text-destructive-foreground"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDelete?.([id])
                      }}
                      title="Delete"
                    >
                      Del
                    </button>
                  </div>
                </div>
              </div>
              {/* Selection checkmark */}
              <div className="absolute top-1 left-1">
                <div
                  className={`h-5 w-5 rounded-sm border flex items-center justify-center text-2xs ${
                    isSelected
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'bg-background/80 border-border text-transparent'
                  }`}
                >
                  ✓
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
